import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Clock, Briefcase, Share2, ArrowLeft, Send, CheckCircle2, ChevronRight, X } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import jobService, { PublicJob } from '../../services/user/jobService';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { toast } from 'react-hot-toast';

const JobDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [job, setJob] = useState<PublicJob | null>(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [heroRef, heroVisible] = useScrollAnimation();

    // Form states
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [resume, setResume] = useState<File | null>(null);
    const [coverLetter, setCoverLetter] = useState('');
    const [formLoading, setFormLoading] = useState(false);

    useEffect(() => {
        if (id) {
            fetchJobDetails(id);
        }
    }, [id]);

    const fetchJobDetails = async (jobId: string) => {
        setLoading(true);
        try {
            const data = await jobService.getJobById(jobId);
            setJob(data);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load job details');
            navigate('/careers');
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !resume) {
            toast.error('Please upload your resume.');
            return;
        }

        setFormLoading(true);
        try {
            await jobService.applyForJob({
                job_id: parseInt(id),
                full_name: fullName,
                email: email,
                phone_number: phone,
                resume: resume,
                cover_letter: coverLetter
            });
            setSubmitted(true);
            toast.success('Application submitted successfully!');
        } catch (err: any) {
            toast.error(err.message || 'Failed to submit application');
        } finally {
            setFormLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <Navbar />
                <div className="flex items-center justify-center py-40">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            </div>
        );
    }

    if (!job) return null;

    return (
        <div className="min-h-screen bg-neutral-50">
            <Navbar />

            {/* Back Button and Header */}
            <div className="max-w-7xl mx-auto px-4 pt-32 pb-0">
                <Link to="/careers" className="inline-flex items-center text-neutral-400 hover:text-[#0c513f] font-bold mb-2 transition-all">
                    <ArrowLeft size={16} className="mr-2" />
                    <span className="text-xs uppercase tracking-widest">Back to All Jobs</span>
                </Link>

                <div className="opacity-100">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex-1 space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-bold rounded-full border border-primary-200 uppercase tracking-wider">
                                    {job.department}
                                </span>
                                <span className="text-neutral-400 text-xs font-medium">
                                    Posted on {new Date(job.created_at).toLocaleDateString()}
                                </span>
                            </div>

                            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 leading-tight">
                                {job.title}
                            </h1>

                            <div className="flex flex-wrap items-center gap-6 text-neutral-600">
                                <div className="flex items-center gap-2">
                                    <MapPin size={20} className="text-primary-500" />
                                    <span className="font-medium">{job.location}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock size={20} className="text-primary-500" />
                                    <span className="font-medium">{job.employment_type}</span>
                                </div>
                                {job.salary && (
                                    <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-lg border border-green-100">
                                        <span className="font-bold">{job.salary}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => setShowForm(true)}
                                className="inline-flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-10 rounded-2xl transition-all shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
                            >
                                Apply Now
                                <ChevronRight size={20} className="ml-1" />
                            </button>
                            <button className="p-4 bg-white hover:bg-neutral-100 text-neutral-600 rounded-2xl border border-neutral-200 transition-all">
                                <Share2 size={24} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <main className="max-w-7xl mx-auto px-4 pt-2 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-12">
                        {/* Description */}
                        <section className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-neutral-100">
                            <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center gap-3">
                                <Briefcase size={28} className="text-primary-500" />
                                Job Overview
                            </h2>
                            <div className="prose prose-lg max-w-none text-neutral-700 whitespace-pre-wrap leading-relaxed">
                                {job.full_description}
                            </div>
                        </section>

                        {/* Responsibilities */}
                        {job.responsibilities && (
                            <section className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-neutral-100">
                                <h2 className="text-2xl font-bold text-neutral-900 mb-6">Responsibilities</h2>
                                <div className="prose prose-lg max-w-none text-neutral-700 whitespace-pre-wrap leading-relaxed">
                                    {job.responsibilities}
                                </div>
                            </section>
                        )}

                        {/* Requirements */}
                        {job.requirements && (
                            <section className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-neutral-100">
                                <h2 className="text-2xl font-bold text-neutral-900 mb-6">Requirements</h2>
                                <div className="prose prose-lg max-w-none text-neutral-700 whitespace-pre-wrap leading-relaxed">
                                    {job.requirements}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="space-y-8">
                        {/* Quick Stats */}
                        <div className="bg-neutral-900 text-white p-8 rounded-3xl shadow-xl sticky top-32 overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary-500 rounded-full opacity-20 blur-2xl"></div>
                            
                            <h3 className="text-xl font-bold mb-6 relative z-10">Job Summary</h3>
                            <div className="space-y-6 relative z-10">
                                <div>
                                    <label className="text-neutral-400 text-xs font-bold uppercase tracking-widest block mb-1">Company</label>
                                    <p className="text-lg font-bold">Rita Platform</p>
                                </div>
                                <div>
                                    <label className="text-neutral-400 text-xs font-bold uppercase tracking-widest block mb-1">Job Type</label>
                                    <p className="text-lg font-bold">{job.employment_type}</p>
                                </div>
                                <div>
                                    <label className="text-neutral-400 text-xs font-bold uppercase tracking-widest block mb-1">Location</label>
                                    <p className="text-lg font-bold">{job.location}</p>
                                </div>
                                <div>
                                    <label className="text-neutral-400 text-xs font-bold uppercase tracking-widest block mb-1">Department</label>
                                    <p className="text-lg font-bold">{job.department}</p>
                                </div>
                            </div>

                            <button
                                onClick={() => setShowForm(true)}
                                className="w-full mt-10 bg-primary-500 hover:bg-primary-600 text-white font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                            >
                                Apply for this position
                                <ChevronRight size={20} />
                            </button>
                        </div>

                        {/* Company Info */}
                        <div className="bg-white p-8 rounded-3xl border border-neutral-100 shadow-sm text-center">
                            <div className="w-20 h-20 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <span className="text-primary-600 font-black text-4xl">R</span>
                            </div>
                            <h3 className="text-xl font-bold text-neutral-900 mb-4">About Rita</h3>
                            <p className="text-neutral-500 mb-6 text-sm">
                                Rita is a universal QR payment and discovery platform that helps customers discover nearby businesses and make instant payments using QR codes.
                            </p>
                            <Link to="/about" className="text-primary-600 font-bold hover:underline inline-flex items-center">
                                Learn more about us
                                <ChevronRight size={16} className="ml-1" />
                            </Link>
                        </div>
                    </aside>
                </div>
            </main>

            {/* Application Modal */}
            {showForm && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-neutral-900/80 backdrop-blur-sm transition-opacity">
                    <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden relative max-h-[95vh] flex flex-col animate-scale-in">
                        {/* Modal Header */}
                        <div className="px-8 py-6 border-b border-neutral-100 flex items-center justify-between sticky top-0 bg-white z-10">
                            <div>
                                <h2 className="text-2xl font-bold text-neutral-900">Application Form</h2>
                                <p className="text-neutral-500 text-sm">{job.title}</p>
                            </div>
                            <button
                                onClick={() => setShowForm(false)}
                                className="p-2 hover:bg-neutral-100 rounded-full text-neutral-400 hover:text-neutral-600 transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-8 pt-6">
                            {submitted ? (
                                <div className="text-center py-12 space-y-6">
                                    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600 animate-bounce">
                                        <CheckCircle2 size={48} />
                                    </div>
                                    <h3 className="text-3xl font-bold text-neutral-900">Application Sent!</h3>
                                    <p className="text-xl text-neutral-500 max-w-md mx-auto">
                                        Thank you for your interest. Our recruitment team will review your application and get back to you soon.
                                    </p>
                                    <button
                                        onClick={() => setShowForm(false)}
                                        className="bg-neutral-900 text-white font-bold py-4 px-10 rounded-2xl hover:bg-primary-600 transition-all mt-6 shadow-xl"
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleApply} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-bold text-neutral-700">Full Name *</label>
                                            <input
                                                required
                                                type="text"
                                                className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 outline-none transition-all placeholder:text-neutral-300"
                                                placeholder="John Doe"
                                                value={fullName}
                                                onChange={(e) => setFullName(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-bold text-neutral-700">Email Address *</label>
                                            <input
                                                required
                                                type="email"
                                                className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 outline-none transition-all placeholder:text-neutral-300"
                                                placeholder="john@example.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-neutral-700">Phone Number *</label>
                                        <input
                                            required
                                            type="tel"
                                            className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 outline-none transition-all placeholder:text-neutral-300"
                                            placeholder="+1 (555) 000-0000"
                                            value={phone}
                                            onChange={(e) => {
                                                const filteredValue = e.target.value.replace(/[^0-9+\- ]/g, '');
                                                setPhone(filteredValue);
                                            }}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-neutral-700">Resume / CV (PDF or DOC) *</label>
                                        <div className="relative group">
                                            <input
                                                required
                                                type="file"
                                                accept=".pdf,.doc,.docx"
                                                onChange={(e) => setResume(e.target.files?.[0] || null)}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />
                                            <div className="w-full px-4 py-10 rounded-2xl border-2 border-dashed border-neutral-200 group-hover:border-primary-400 group-hover:bg-primary-50/50 flex flex-col items-center justify-center transition-all">
                                                <Send size={32} className="text-neutral-300 group-hover:text-primary-500 mb-2" />
                                                <span className="text-sm font-semibold text-neutral-500 group-hover:text-primary-600">
                                                    {resume ? resume.name : 'Click to upload or drag and drop'}
                                                </span>
                                                <span className="text-xs text-neutral-400 mt-1">PDF, DOC, DOCX up to 10MB</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-neutral-700">Cover Letter (Optional)</label>
                                        <textarea
                                            rows={4}
                                            className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:ring-2 focus:ring-primary-500 outline-none transition-all placeholder:text-neutral-300 resize-none"
                                            placeholder="Tell us why you're a great fit..."
                                            value={coverLetter}
                                            onChange={(e) => setCoverLetter(e.target.value)}
                                        ></textarea>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={formLoading}
                                        className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-neutral-400 text-white font-bold py-4 rounded-xl transition-all shadow-xl hover:shadow-2xl active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        {formLoading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                Submit Application
                                                <Send size={18} />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default JobDetails;
